import type { ChangeEvent, DragEvent, FC } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import visuallyHidden from "@mui/utils/visuallyHidden";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { CircularProgress } from "@privacy/ui";
import type { TranslationKey } from "@privacy/translations";
import type { FileOwner, UploadedFile } from "../../api/files";
import { uploadFileMutationOptions } from "../../api/files";
import type { FileReference } from "../types";
import { hasErrorCode } from "../../api/errors";
import * as FormDialog from "../../shared/FormDialog";
import { FileTextIcon, UploadIcon } from "../../shared/icons";
import { BYTES_PER_MEGABYTE, formatFileSize } from "../../shared/formatFileSize";
import { useSpinDelay } from "../../shared/useSpinDelay";

const ACCEPTED_EXTENSIONS = [
  "pdf",
  "docx",
  "xlsx",
  "pptx",
  "odt",
  "ods",
  "odp",
  "txt",
  "csv",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
];

const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map(extension => `.${extension}`).join(",");

const MAX_FILE_SIZE_IN_BYTES = 25 * BYTES_PER_MEGABYTE;

const getUploadErrorMessageKey = (error: unknown): TranslationKey => {
  if (hasErrorCode(error, "FILE_SINGLE_FILE_REQUIRED")) return "Document.Error.OneFileAtATime";
  if (hasErrorCode(error, "FILE_TOO_LARGE")) return "Document.Error.TooLarge";
  if (hasErrorCode(error, "FILE_TYPE_NOT_ALLOWED")) return "Document.Error.TypeNotAllowed";
  if (hasErrorCode(error, "FILE_SIGNATURE_INVALID")) return "Document.Error.SignatureInvalid";
  if (hasErrorCode(error, "FILE_TITLE_TOO_LONG")) return "Document.Error.FileNameTooLong";
  if (hasErrorCode(error, "FILE_ORGANISATION_STORAGE_LIMIT_EXCEEDED")) return "Document.Error.StorageLimitExceeded";
  if (hasErrorCode(error, "FILE_UPLOAD_BLOCKED")) return "Document.Error.UploadBlocked";
  return "Document.Error.UploadFailed";
};

const hasAcceptedExtension = (fileName: string) =>
  ACCEPTED_EXTENSIONS.includes(fileName.split(".").pop()?.toLowerCase() ?? "");

type FilePickerProps = {
  label: string;
  fileOwner: FileOwner;
  questionId: string;
  file: Pick<FileReference, "fileName"> | null;
  onChange: (file: UploadedFile) => void;
  validationError?: string;
};

export const FilePicker: FC<FilePickerProps> = ({ label, fileOwner, questionId, file, onChange, validationError }) => {
  const { t, language } = useTranslation();
  const [pickedFileName, setPickedFileName] = useState("");
  const [refusalMessage, setRefusalMessage] = useState<string>();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const uploadFileMutation = useMutation(uploadFileMutationOptions());
  const id = useId();
  const errorId = `${id}-error`;
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController>(null);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const maxSize = formatFileSize(MAX_FILE_SIZE_IN_BYTES, language);

  const getRefusalMessage = (files: FileList) => {
    if (files.length > 1) return t("Document.Error.OneFileAtATime");
    if (!hasAcceptedExtension(files[0].name)) return t("Document.Error.TypeNotAllowed");
    if (files[0].size > MAX_FILE_SIZE_IN_BYTES) return t("Document.Error.TooLarge", { maxSize });
    return undefined;
  };

  const upload = (files: FileList) => {
    if (files.length === 0) return;
    const refusal = getRefusalMessage(files);
    setRefusalMessage(refusal);
    if (refusal) return;

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setPickedFileName(files[0].name);
    uploadFileMutation.mutate(
      { owner: fileOwner, questionId, file: files[0], signal: abortController.signal },
      { onSuccess: onChange }
    );
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) upload(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(false);
    upload(event.dataTransfer.files);
  };

  const serverErrorMessage = uploadFileMutation.isError
    ? t(getUploadErrorMessageKey(uploadFileMutation.error), { maxSize })
    : undefined;
  const uploadErrorMessage = refusalMessage ?? serverErrorMessage;
  const errorMessage = uploadErrorMessage ?? validationError;

  const showsDropPrompt = (!file && !uploadFileMutation.isPending) || isDraggingOver;
  const isUploadSpinnerVisible = useSpinDelay(uploadFileMutation.isPending, { delay: 200 });
  const getStatus = (): PickerStatus => {
    if (showsDropPrompt) return "empty";
    if (isUploadSpinnerVisible) return "uploading";
    return "file";
  };
  const shownFileName = uploadFileMutation.isPending ? pickedFileName : (file?.fileName ?? "");

  return (
    <FormDialog.Field errorMessage={errorMessage} errorId={errorId}>
      <FormDialog.Label>{label}</FormDialog.Label>
      <Box
        onDragOver={event => {
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsDraggingOver(false);
        }}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          p: 4,
          borderRadius: 1,
          border: "1px dashed",
          borderColor: theme => theme.palette.primary.main,
          backgroundColor: theme => (isDraggingOver ? theme.palette.action.selected : "transparent"),
          boxShadow: theme => theme.shadows[1],
          transition: "background-color 150ms ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: theme => theme.palette.action.hover,
            color: theme => theme.palette.primary.main,
          }}
        >
          <StatusIcon status={getStatus()} />
        </Box>
        {showsDropPrompt && (
          <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600 }}>
            {isDraggingOver ? t("Document.File.DropActive") : t("Document.File.DropTitle")}
          </Typography>
        )}
        {!showsDropPrompt && <FileName name={shownFileName} />}
        <Box role="status" sx={visuallyHidden}>
          {uploadFileMutation.isPending && t("Document.File.Uploading")}
          {uploadFileMutation.isSuccess && t("Document.File.Uploaded")}
        </Box>
        <Button
          size="small"
          variant="outlined"
          aria-describedby={errorMessage ? errorId : undefined}
          onClick={() => inputRef.current?.click()}
          sx={{ minWidth: 120, mixBlendMode: "multiply" }}
        >
          {file ? t("Document.File.Replace") : t("Document.File.Choose")}
        </Button>
        <input ref={inputRef} type="file" hidden accept={ACCEPT_ATTRIBUTE} onChange={handleInputChange} />
        <Typography
          sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary", textAlign: "center" }}
        >
          {t("Document.File.AllowedTypes", { maxSize })}
        </Typography>
      </Box>

      <Box role="alert" sx={visuallyHidden}>
        {uploadErrorMessage}
      </Box>
    </FormDialog.Field>
  );
};

type PickerStatus = "empty" | "uploading" | "file";

const StatusIcon: FC<{ status: PickerStatus }> = ({ status }) => {
  const { t } = useTranslation();
  if (status === "empty") return <UploadIcon size={18} />;
  if (status === "uploading") return <CircularProgress size={18} aria-label={t("Document.File.Uploading")} />;
  return <FileTextIcon size={18} />;
};

const FileName: FC<{ name: string }> = ({ name }) => (
  <Typography
    sx={{
      maxWidth: "100%",
      textAlign: "center",
      overflowWrap: "anywhere",
      fontSize: theme => theme.typography.pxToRem(14),
      fontWeight: 600,
    }}
  >
    {name}
  </Typography>
);

export const useIgnoreFileDropOutsideDropArea = () => {
  useEffect(() => {
    const ignoreFileDropOutsideDropArea = (event: globalThis.DragEvent) => {
      if (event.defaultPrevented) return;
      if (!event.dataTransfer?.types.includes("Files")) return;
      event.preventDefault();
      if (event.type === "dragover") event.dataTransfer.dropEffect = "none";
    };
    window.addEventListener("dragover", ignoreFileDropOutsideDropArea);
    window.addEventListener("drop", ignoreFileDropOutsideDropArea);
    return () => {
      window.removeEventListener("dragover", ignoreFileDropOutsideDropArea);
      window.removeEventListener("drop", ignoreFileDropOutsideDropArea);
    };
  }, []);
};
